import {
  ReactNode,
  default as React,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { MarkdownViewer, markdownToTextPreview } from '@ardoq/markdown';
import { Island, IslandProps } from './Island';
import styled, { css } from 'styled-components';
import { normal14 } from '@ardoq/typography';
import { colors, s16 } from '@ardoq/design-tokens';
import { Notification } from '@ardoq/status-ui';
import { BUTTON_SELECTOR } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';

const Expander = styled.button`
  color: ${colors.textAction};
  ${normal14};
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
  border: 0;
  background: ${colors.surfaceDefault};
  text-align: left;
  width: fit-content;
  cursor: pointer;
  &:hover,
  &:focus {
    color: ${colors.textActionHover};
  }
`;

const ErrorNotification = styled(Notification)`
  margin: ${s16} 0px;
`;

const MarkdownPreviewWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 12px;
  align-items: start;
`;

const MarkdownPreview = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const DescriptionPlaceholder = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  color: ${colors.textSubtle};
`;

const IslandActionsButtonGroup = styled(FlexBox).attrs({
  gap: 'small',
  align: 'center',
})`
  align-self: flex-start;
`;

const PageHeaderContainer = styled.div<{
  $showButtonsOnlyOnHover: boolean;
}>`
  width: 100%;
  ${({ $showButtonsOnlyOnHover }) =>
    $showButtonsOnlyOnHover &&
    css`
      ${IslandActionsButtonGroup} ${BUTTON_SELECTOR} {
        visibility: hidden;
      }

      &:hover ${IslandActionsButtonGroup} ${BUTTON_SELECTOR} {
        visibility: visible;
      }
    `}
`;

export type PageHeaderIslandProps = {
  description?: string;
  descriptionPlaceholder?: string;
  buttons?: ReactNode;
  errorMessage?: string;
  showButtonsOnlyOnHover?: boolean;
  trackEventExpandDescription?: (isExpanded: boolean) => void;
  showExpandDescriptionButton?: boolean;
} & Omit<IslandProps, 'subtitle' | 'rightContent'>;

export const PageHeaderIsland = ({
  description,
  descriptionPlaceholder,
  buttons,
  errorMessage,
  maxWidth,
  showButtonsOnlyOnHover = false,
  trackEventExpandDescription,
  showExpandDescriptionButton,
  ...props
}: PageHeaderIslandProps) => {
  const [descriptionPreview, setDescriptionPreview] = useState<string>();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [enableShowMore, setEnableShowMore] = useState(false);

  const previewRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const preview = description && markdownToTextPreview(description);

    const descriptionNormalized =
      description && description.replace(/\.\.\./g, '…');

    const hasOverflow =
      !!previewRef.current &&
      previewRef.current.scrollWidth >=
        previewRef.current.parentElement!.offsetWidth;

    if (preview) setDescriptionPreview(preview);

    setEnableShowMore(
      !isDescriptionExpanded &&
        preview === descriptionPreview &&
        (descriptionNormalized !== preview || hasOverflow)
    );
  }, [description, descriptionPreview, isDescriptionExpanded]);

  return (
    <PageHeaderContainer $showButtonsOnlyOnHover={showButtonsOnlyOnHover}>
      <Island
        maxWidth={maxWidth}
        subtitleWidth={'large'}
        subtitle={
          <>
            {errorMessage && (
              <ErrorNotification error>{errorMessage}</ErrorNotification>
            )}
            {!description && !!descriptionPlaceholder && (
              <DescriptionPlaceholder>
                {descriptionPlaceholder}
              </DescriptionPlaceholder>
            )}
            {description && !isDescriptionExpanded && (
              <MarkdownPreviewWrapper>
                <MarkdownPreview ref={previewRef}>
                  {descriptionPreview}
                </MarkdownPreview>
                {showExpandDescriptionButton && enableShowMore && (
                  <Expander
                    onClick={() => {
                      trackEventExpandDescription?.(true);
                      setIsDescriptionExpanded(true);
                    }}
                  >
                    Show more
                  </Expander>
                )}
              </MarkdownPreviewWrapper>
            )}
            {description && isDescriptionExpanded && (
              <>
                <MarkdownViewer
                  content={description ?? ''}
                  sanitationLevel="permissive"
                />
                {showExpandDescriptionButton && (
                  <Expander
                    onClick={() => {
                      trackEventExpandDescription?.(true);
                      setIsDescriptionExpanded(false);
                    }}
                  >
                    Show less
                  </Expander>
                )}
              </>
            )}
          </>
        }
        rightContent={
          buttons ? (
            <IslandActionsButtonGroup>{buttons}</IslandActionsButtonGroup>
          ) : null
        }
        {...props}
      />
    </PageHeaderContainer>
  );
};
